<template>
  <div :class="allClasses" data-test-id="thumbnail">
    <img
      v-if="!fallbackShown && displayedSrc"
      :src="displayedSrc"
      class="thumbnail__image"
      :alt="alt"
      :aria-hidden="ariaHidden"
      data-test-id="thumbnail-image"
      @error="enableFallback"
    >
    <template v-if="fallbackShown">
      <EFallbackImageTrees
        v-if="fallback === 'trees'"
        class="thumbnail__fallback"
        data-test-id="thumbnail-fallback-trees"
      />
      <EFallbackImageDefault
        v-else
        class="thumbnail__fallback"
        data-test-id="thumbnail-fallback-default"
      />
    </template>
    <slot />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { privacyProxy } from 'common-js/constants/feature-flags';
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import EFallbackImageDefault from '~~/common/vue/assets/images/placeholder-landscape.svg';
import EFallbackImageTrees from '~~/common/vue/assets/images/placeholder-trees.svg';
import { getProxiedUrl } from '~~/search/common/js/universal/urls';

const fitOptions = ['cover', 'contain'];
const OBSERVER_ROOT_MARGIN = '100px 100px 100px 100px';

export default {
  name: 'EThumbnail',
  components: {
    EFallbackImageTrees,
    EFallbackImageDefault,
  },
  mixins: [classes],
  props: {
    alt: {
      type: String,
      default: '',
    },
    fallbackBackground: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    fit: {
      type: String,
      default: 'cover',
      validator: (option) => fitOptions.includes(option),
      cssClass: true,
    },
    imageOverlay: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    prefetch: {
      type: Boolean,
      default: true,
    },
    src: {
      type: String,
      default: '',
    },
    fallback: {
      type: String,
      default: 'default',
    },
  },
  data: () => ({
    fallbackShown: false,
    displayedSrc: null,
  }),
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    allClasses() {
      const allClasses = [...this.classes];
      if (this.fallbackShown) {
        allClasses.push(getModifierClass('thumbnail', 'fallback'));
      }
      return allClasses;
    },
    ariaHidden() {
      return this.alt ? 'false' : 'true';
    },
    shouldProxy() {
      return this.getFeatureToggle(privacyProxy.flag)?.value === privacyProxy.enabled;
    },
  },
  created() {
    if (!this.prefetch) {
      this.displayedSrc = this.shouldProxy ? getProxiedUrl(this.src) : this.src;
    }
  },
  async mounted() {
    if (!this.src) {
      this.enableFallback();
      return;
    }
    if (this.prefetch) {
      this.initPreFetch();
    }
  },
  methods: {
    async initPreFetch() {
      if (!window.IntersectionObserver) {
        this.displayedSrc = await this.preFetchImage();
        this.$emit('load');
        return;
      }

      // if the IO API is available, start fetching once the image enters the viewport
      // (this also works in nested scrollable containers like carousels)
      const intersectionObserved = async ([entry], observer) => {
        if (entry.isIntersecting) {
          try {
            this.displayedSrc = await this.preFetchImage();
            this.$emit('load');
          } catch (err) {
            this.enableFallback();
          }
          observer.unobserve?.(this.$el);
        }
      };

      const options = { rootMargin: OBSERVER_ROOT_MARGIN };
      const observer = new IntersectionObserver(intersectionObserved, options);
      observer.observe(this.$el);
    },
    async preFetchImage() {
      // here we pre-fetch the image to check if the server
      // is actually returning its own fallback with a 400
      // status code. If it isn't, we load the image as an ArrayBuffer,
      // and display it using a Blob URL to avoid a second HTTP request
      let response = null;

      // if our privacy proxy experiment is active,
      // we try with the proxied url first.
      if (this.shouldProxy) {
        response = await window.fetch?.(getProxiedUrl(this.src));
      }

      // if the proxy isn't active or failedfor any reason, we'll try again with the original url
      // as a fallback without activating the fallback images yet
      if (!response?.ok) {
        response = await window.fetch?.(this.src);
      }

      // if all prefetch tries failed, we activate the fallback image
      if (!response?.ok) {
        this.enableFallback();
        return null;
      }

      const type = response.headers.get('content-type');
      const buffer = await response.arrayBuffer();
      const blob = new Blob([buffer], { type });
      return URL.createObjectURL?.(blob);
    },
    enableFallback() {
      // special case:
      // if there is no prefetch active, a possible error with loading the img
      // using the proxy hasn't been caught and handled yet.
      // In this case, we set the displayedSrc as the original src,
      // only if it isn't already equal (to prevent the fallback from never being shown
      // in case the actual src also responds with an error code).
      const initialFailWithProxy = !this.prefetch &&
                                    this.shouldProxy &&
                                    this.displayedSrc !== this.src;

      if (initialFailWithProxy) {
        this.displayedSrc = this.src;
      } else {
        this.fallbackShown = true;
        this.$emit('error');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.thumbnail__image {
  // according to design spec,
  // alt text will only be accessible to screen readers :(
  // TODO: see if we can do better
  color: transparent;
  width: 100%;
  height: 100%;
}

.thumbnail--fit-contain {
  height: 100%;

  .thumbnail__image {
    object-fit: contain;
  }
}

.thumbnail--fit-cover {
  height: 100%;

  .thumbnail__image {
    object-fit: cover;
  }
}

.thumbnail__fallback {
  width: 100%;
  height: 100%;
  color: var(--color-decorative-icon);
  background-color: var(--color-background-quaternary);
}

.thumbnail--fallback.thumbnail--fallback-background {
  background-color: var(--color-background-secondary);
}

.thumbnail--image-overlay::after {
  content: '';
  position: absolute;
  background: scale-color($c-gray-90, $alpha: -98%);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}
</style>
